import { corsUrl, loadBitmap, type AssetCatalog } from './assets';

/** Um recorte de uma folha de interface (o que o pipeline extraiu do SWF original). */
export interface UiFrame {
  name: string;
  /** Posição e tamanho do recorte na folha. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Tamanho original do desenho (com a margem transparente que o empacotador cortou) e onde o recorte fica dentro dele. */
  sourceW: number;
  sourceH: number;
  offX: number;
  offY: number;
}

interface RawFrame {
  frame: { x: number; y: number; w: number; h: number };
  rotated?: boolean;
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
}

const UI_ROOT = 'atlas/swf/Arquivos_para_IIS/ui/portugal/swf';

/**
 * Caminho lógico (sem extensão) de uma folha de interface pelo nome do módulo do cliente original (`shop`, `RoomList`, `room-0`, `game`...).
 * Módulos grandes vêm em várias folhas (`room-0`, `room-1`).
 */
export function uiAtlasPath(name: string): string {
  const dir = name.replace(/-\d+$/, '');
  return `${UI_ROOT}/${dir}/Arquivos_para_IIS_ui_portugal_swf_${name}`;
}

/**
 * Folha de interface do cliente original (janelas, botões, painéis, ícones): um WebP com os recortes e um JSON com o nome e a posição de
 * cada um. O layout das telas (onde cada peça fica) não está no pacote: quem monta a tela escolhe os recortes pelo nome.
 */
export class UiAtlas {
  readonly frames = new Map<string, UiFrame>();
  private blobs = new Map<string, Promise<string | null>>();
  private bitmap: Promise<ImageBitmap | null> | null = null;

  private constructor(
    readonly name: string,
    readonly sheetUrl: string,
    readonly width: number,
    readonly height: number,
    private readonly assets: AssetCatalog,
  ) {}

  /** `null` se o módulo não existe no manifest. */
  static async load(assets: AssetCatalog, name: string, fetchImpl: typeof fetch = (...args) => fetch(...args)): Promise<UiAtlas | null> {
    const base = uiAtlasPath(name);
    if (!assets.has(`${base}.json`) || !assets.has(`${base}.webp`)) return null;
    const response = await fetchImpl(corsUrl(assets.url(`${base}.json`)));
    if (!response.ok) return null;
    const json = (await response.json()) as { frames: Record<string, RawFrame>; meta?: { size?: { w: number; h: number } } };
    const atlas = new UiAtlas(name, assets.url(`${base}.webp`), json.meta?.size?.w ?? 0, json.meta?.size?.h ?? 0, assets);
    for (const [frameName, raw] of Object.entries(json.frames)) {
      if (raw.rotated) continue;
      const source = raw.sourceSize ?? { w: raw.frame.w, h: raw.frame.h };
      const offset = raw.spriteSourceSize ?? { x: 0, y: 0, w: raw.frame.w, h: raw.frame.h };
      atlas.frames.set(frameName, { name: frameName, x: raw.frame.x, y: raw.frame.y, w: raw.frame.w, h: raw.frame.h, sourceW: source.w, sourceH: source.h, offX: offset.x, offY: offset.y });
    }
    return atlas;
  }

  /** O recorte pelo nome exato, ou o primeiro cujo nome termina com `suffix` (ex.: `asset.shop.RightViewBg`; os nomes vêm como `62_asset.shop.RightViewBg`). */
  frame(name: string): UiFrame | undefined {
    const exact = this.frames.get(name);
    if (exact) return exact;
    for (const [key, value] of this.frames) if (key.endsWith(`_${name}`) || key === name) return value;
    return undefined;
  }

  /** Imagem PNG (blob:) só com o recorte, no tamanho original (com a margem transparente). Serve para `border-image` e `<img>`. */
  blob(name: string): Promise<string | null> {
    let cached = this.blobs.get(name);
    if (!cached) {
      cached = this.render(name).catch(() => null);
      this.blobs.set(name, cached);
    }
    return cached;
  }

  private sheet(): Promise<ImageBitmap | null> {
    this.bitmap ??= loadBitmap(this.sheetUrl);
    return this.bitmap;
  }

  private async render(name: string): Promise<string | null> {
    const f = this.frame(name);
    const sheet = await this.sheet();
    if (!f || !sheet) return null;
    const canvas = document.createElement('canvas');
    canvas.width = f.sourceW;
    canvas.height = f.sourceH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(sheet, f.x, f.y, f.w, f.h, f.offX, f.offY, f.w, f.h);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    return blob ? URL.createObjectURL(blob) : null;
  }
}
